import { useCallback, useEffect, useState } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';

export default function useMultiselectChips({
  labelsHash,
  onSearchChanged,
  onValuesChanged,
  options,
  sortOptions,
  values,
}) {
  const [selectedValues, setSelectedValues] = useState(values);
  const [targetRef, setTargetRef] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [filterValue, setFilterValue] = useState('');

  // if an external option getter is provided, a situation may arise when some search will result in empty options.
  // Since the labels of selected items are option-dependent and to prevent the labels from being reset,
  // a hash of memo labels is used
  const [memoizedLabels, setMemoizedLabels] = useState({});

  const closeOptions = () => setIsOpen(false);
  const openOptions = () => setIsOpen(true);

  useEffect(() => {
    const unselectedOptions = reject(options, option => includes(selectedValues, option.value));
    let optionsToSet = filterValue
      ? unselectedOptions.filter(option =>
          (get(option, 'label', '') + get(option, 'description', '')).toLowerCase().includes(filterValue.toLowerCase())
        )
      : unselectedOptions;

    if (sortOptions) {
      optionsToSet = orderBy(optionsToSet, ['label', 'description'], ['asc', 'asc']);
    }
    setAvailableOptions(optionsToSet);

    const memoizedLabelsToSet = reduce(
      selectedValues,
      (memo, value) => ({
        ...memo,
        [value]: get(find(options, { value }), 'label', memo[value]),
      }),
      memoizedLabels
    );

    setMemoizedLabels(memoizedLabelsToSet);
  }, [filterValue, options, selectedValues]);

  useEffect(() => {
    setMemoizedLabels({ ...memoizedLabels, ...labelsHash });
  }, [labelsHash]);

  const onInputBlur = () => {
    setSelectedIndex(-1);
  };

  const onTextChange = event => {
    const { value } = event.target;

    setSelectedIndex(-1);
    openOptions();
    setFilterValue(value);
    onSearchChanged && onSearchChanged(value);
  };

  const addItem = valueToAdd => {
    const updatedValues = [...selectedValues, valueToAdd];
    setSelectedValues(updatedValues);
    onValuesChanged && onValuesChanged(updatedValues);
    targetRef.focus();
  };

  const onDelete = value => {
    const updatedValues = selectedValues.filter(v => v !== value);
    setSelectedValues(updatedValues);
    onValuesChanged && onValuesChanged(updatedValues);
    targetRef.focus();
  };

  const changeItemPosition = (currentItemId, replaceItemId) => {
    const updatedValues = [...selectedValues];
    const currentItemIndex = updatedValues.indexOf(currentItemId);
    if (currentItemIndex === -1) {
      return;
    }
    const removedItems = updatedValues.splice(currentItemIndex, 1);
    const currentItem = removedItems[0];
    const toIndex = updatedValues.indexOf(replaceItemId);
    if (toIndex === -1) {
      return;
    }
    const updatedCurrentItemIndex = toIndex >= currentItemIndex ? toIndex + 1 : toIndex;
    updatedValues.splice(updatedCurrentItemIndex, 0, currentItem);

    setSelectedValues(updatedValues);
    onValuesChanged && onValuesChanged(updatedValues);
  };

  const onKeyDown = useCallback(
    event => {
      switch (event.key) {
        case 'Enter':
          event.stopPropagation();
          event.preventDefault();
          if (selectedIndex !== -1) {
            availableOptions[selectedIndex] && addItem(availableOptions[selectedIndex].value);
          }
          closeOptions();
          onInputBlur();
          return false;
        case 'Escape':
          event.stopPropagation();
          closeOptions();
          onInputBlur();
          return false;
        case 'ArrowDown':
          if (!isOpen) {
            openOptions();
          }

          if (selectedIndex < availableOptions.length - 1) {
            setSelectedIndex(selectedIndex + 1);
          }
          break;
        case 'ArrowUp':
          if (selectedIndex) {
            setSelectedIndex(selectedIndex - 1);
          }
          break;
        case 'Backspace':
          if (!isOpen && !targetRef.value && selectedValues.length) {
            onDelete(selectedValues[selectedValues.length - 1]);
          }
          break;
      }
      return true;
    },
    [addItem, availableOptions, onDelete, onInputBlur, selectedIndex, selectedValues, setSelectedIndex]
  );

  return {
    addItem,
    availableOptions,
    closeOptions,
    filterValue,
    isOpen,
    memoizedLabels,
    changeItemPosition,
    onDelete,
    onInputBlur,
    onKeyDown,
    onTextChange,
    openOptions,
    selectedIndex,
    selectedValues,
    setTargetRef,
    targetRef,
  };
}
